import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import DashboardLayout from '../../components/common/DashboardLayout';
import AdminNav from '../../components/admin/AdminNav';
import RichTextEditor from '../../components/common/RichTextEditor';
import VideoTopicPicker from '../../components/admin/VideoTopicPicker';
import MediaDropZone from '../../components/admin/MediaDropZone';
import CourseStudents from '../../components/admin/CourseStudents';
import {
  getAdminCourseDetail, updateAdminCourse, listAdminCourses,
  createAdminLesson, updateAdminLesson, deleteAdminLesson,
} from '../../services/adminCourse.service';
import { getSavedImageAltText } from '../../services/adminSequenceContent.service';

// Jenn's real Vimeo links are the unlisted/private-share format
// (vimeo.com/{id}/{hash}) — the hash is required for the video to actually
// play. Lets the admin paste the full link Vimeo gives on "Copy link"
// rather than hand-splitting it into two fields.
// Mirrors server/src/utils/driveImage.js's normalizeImageUrl — a raw Drive
// "Copy link" URL opens the viewer page, not the image, so the live preview
// needs the same thumbnail-endpoint rewrite the server applies on save.
const DRIVE_FILE_PATTERNS = [
  /drive\.google\.com\/file\/d\/([\w-]+)/,
  /drive\.google\.com\/open\?id=([\w-]+)/,
  /drive\.google\.com\/uc\?[^ ]*[?&]id=([\w-]+)/,
];
const previewImageUrl = (url) => {
  if (!url) return url;
  for (const re of DRIVE_FILE_PATTERNS) {
    const m = url.match(re);
    if (m) return `https://drive.google.com/thumbnail?id=${m[1]}&sz=w1600`;
  }
  return url;
};


const parseVimeoInput = (input) => {
  const trimmed = (input || '').trim();
  if (!trimmed) return { id: null, hash: null };
  const m = trimmed.match(/vimeo\.com\/(?:video\/)?(\d+)(?:\/([a-zA-Z0-9]+))?/i)
    || trimmed.match(/^(\d+)(?:\/([a-zA-Z0-9]+))?$/);
  if (m) return { id: m[1], hash: m[2] || null };
  return { id: null, hash: null };
};

export default function AdminCourseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [course, setCourse]   = useState(null);
  const [allCourses, setAllCourses] = useState([]);
  const [error, setError]     = useState(null);
  const [addingLesson, setAddingLesson] = useState(false);

  const load = useCallback(async () => {
    try {
      setError(null);
      const [courseData, coursesData] = await Promise.all([
        getAdminCourseDetail(id),
        listAdminCourses(),
      ]);
      setCourse(courseData);
      setAllCourses(coursesData);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load this course.');
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (error) {
    return (
      <DashboardLayout>
        <div className="max-w-3xl mx-auto bg-red-50 border border-red-200 rounded-2xl px-4 py-3 text-sm text-red-700">
          {error}
          <Link to="/admin/courses" className="block mt-2 underline">Back to courses</Link>
        </div>
      </DashboardLayout>
    );
  }

  if (!course) {
    return (
      <DashboardLayout>
        <div className="max-w-3xl mx-auto text-sm text-gray-400">Loading…</div>
      </DashboardLayout>
    );
  }

  const otherCourses = allCourses.filter(c => c.id !== course.id);
  const sortedLessons = [...course.lessons].sort((a, b) => a.display_order - b.display_order);

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto">
        <AdminNav />

        <button onClick={() => navigate('/admin/courses')} className="text-xs text-gray-400 hover:text-reset-brown mb-3">
          ← All courses
        </button>

        <h1 className="font-serif text-2xl font-bold text-reset-brown mb-6">{course.title}</h1>

        <CourseMetaForm course={course} otherCourses={otherCourses} onSaved={load} />

        <div className="flex items-center justify-between mt-8 mb-3">
          <h2 className="text-base font-bold text-reset-brown">
            Lessons ({sortedLessons.length})
          </h2>
          <button
            onClick={() => setAddingLesson(v => !v)}
            className="px-3 py-1.5 bg-reset-terracotta text-white text-xs font-medium rounded-lg hover:bg-reset-brown transition-colors"
          >
            {addingLesson ? 'Cancel' : '+ Add lesson'}
          </button>
        </div>

        {addingLesson && (
          <LessonForm
            courseId={course.id}
            nextOrder={sortedLessons.length + 1}
            onSaved={() => { setAddingLesson(false); load(); }}
            onCancel={() => setAddingLesson(false)}
          />
        )}

        <div className="space-y-3">
          {sortedLessons.map(lesson => (
            <LessonRow key={lesson.id} lesson={lesson} onChanged={load} />
          ))}
          {sortedLessons.length === 0 && !addingLesson && (
            <p className="text-sm text-gray-400 bg-white rounded-2xl shadow-sm px-5 py-6 text-center">
              No lessons yet — add the first one above.
            </p>
          )}
        </div>

        <CourseStudents courseId={course.id} />
      </div>
    </DashboardLayout>
  );
}

// ─── Course meta form ────────────────────────────────────────────────────────

function CourseMetaForm({ course, otherCourses, onSaved }) {
  const [title, setTitle] = useState(course.title);
  const [description, setDescription] = useState(course.description || '');
  const [instructorName, setInstructorName] = useState(course.instructor_name || '');
  const [coverImageUrl, setCoverImageUrl] = useState(course.cover_image_url || '');
  const [displayOrder, setDisplayOrder] = useState(course.display_order);
  const [categories, setCategories] = useState((course.categories || []).join(', '));
  const [isPublished, setIsPublished] = useState(course.is_published);
  const [comingSoon, setComingSoon] = useState(!!course.coming_soon);
  const [unlockAfterCourseId, setUnlockAfterCourseId] = useState(course.unlock_after_course_id || '');
  const [unlockAfterStatus, setUnlockAfterStatus] = useState(course.unlock_after_status || 'started');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaved(false);
    try {
      await updateAdminCourse(course.id, {
        title: title.trim(),
        instructor_name: instructorName.trim() || null,
        description: description.trim() || null,
        cover_image_url: coverImageUrl.trim() || null,
        display_order: Number(displayOrder) || 0,
        categories: categories.split(',').map(c => c.trim()).filter(Boolean),
        is_published: isPublished,
        coming_soon: comingSoon,
        unlock_after_course_id: unlockAfterCourseId || null,
        unlock_after_status: unlockAfterCourseId ? unlockAfterStatus : null,
      });
      setSaved(true);
      onSaved();
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl shadow-sm p-5 space-y-3">
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Title</label>
        <input
          value={title} onChange={e => setTitle(e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
        />
      </div>
      {/* Jenn, 2026-10-26: courses had no instructor — e.g. Fireside Conversations with Trish is all Trish's content. */}
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Instructor — shown to members on the course (e.g. Trish)</label>
        <input
          value={instructorName} onChange={e => setInstructorName(e.target.value)} placeholder="Who leads this course"
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Description</label>
        <textarea
          value={description} onChange={e => setDescription(e.target.value)} rows={2}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Cover image — drag a picture in (a link still works too)</label>
        <input
          value={coverImageUrl} onChange={e => setCoverImageUrl(e.target.value)}
          placeholder="https://…"
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
        />
        <MediaDropZone kind="image" onUploaded={setCoverImageUrl} />
        {coverImageUrl && (
          <img src={previewImageUrl(coverImageUrl)} alt="" className="mt-2 w-36 aspect-video object-contain bg-reset-terracotta/10 rounded-lg border border-gray-200" />
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Display order</label>
          <input
            type="number" value={displayOrder} onChange={e => setDisplayOrder(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
          />
        </div>
        {course.type === 'micro' && (
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Categories (comma-separated)</label>
            <input
              value={categories} onChange={e => setCategories(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
            />
          </div>
        )}
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Unlocks after course</label>
          <select
            value={unlockAfterCourseId} onChange={e => setUnlockAfterCourseId(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
          >
            <option value="">No gate — available immediately</option>
            {otherCourses.map(c => (
              <option key={c.id} value={c.id}>{c.title}</option>
            ))}
          </select>
        </div>
        {unlockAfterCourseId && (
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Unlocks when that course is…</label>
            <select
              value={unlockAfterStatus} onChange={e => setUnlockAfterStatus(e.target.value)}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
            >
              <option value="started">Started</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        )}
      </div>

      <label className="flex items-center gap-2 text-sm text-gray-600">
        <input type="checkbox" checked={isPublished} onChange={e => setIsPublished(e.target.checked)} />
        Published (visible to members)
      </label>
      {!isPublished && (
        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input type="checkbox" checked={comingSoon} onChange={e => setComingSoon(e.target.checked)} />
          Show as "Coming soon" (members see only the picture and title)
        </label>
      )}

      <div className="flex items-center gap-3">
        <button
          type="submit" disabled={saving}
          className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save course'}
        </button>
        {saved && <span className="text-xs text-green-600">Saved</span>}
      </div>
    </form>
  );
}

// ─── Lesson row (collapsed, expands to LessonForm) ──────────────────────────

function LessonRow({ lesson, onChanged }) {
  const [editing, setEditing] = useState(false);

  const remove = async () => {
    if (!window.confirm(`Delete lesson "${lesson.title}"? This can't be undone.`)) return;
    await deleteAdminLesson(lesson.id);
    onChanged();
  };

  if (editing) {
    return (
      <LessonForm
        lesson={lesson}
        courseId={lesson.course_id}
        onSaved={() => { setEditing(false); onChanged(); }}
        onCancel={() => setEditing(false)}
      />
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm px-5 py-3.5 flex items-center justify-between gap-3">
      <div className="min-w-0">
        {lesson.section_title && (
          <p className="text-[10px] font-medium text-reset-terracotta uppercase tracking-wide">{lesson.section_title}</p>
        )}
        <p className="text-sm font-semibold text-reset-brown">
          {lesson.display_order}. {lesson.title}
        </p>
        <p className="text-xs text-gray-400 mt-0.5">
          {lesson.video_bunny_id ? `Bunny ${lesson.video_bunny_id}` : lesson.video_vimeo_id ? `Vimeo ${lesson.video_vimeo_id}` : 'No video yet'}
          {lesson.video_duration_seconds ? ` · ${Math.round(lesson.video_duration_seconds / 60)}m` : ''}
        </p>
        {/* Jenn, 2026-08-27: "some of the lessons have the exercise field
            and recommended resources, some only have one" — every lesson's
            edit form always shows both fields, so this is a content gap,
            not a form bug. These badges make the gaps visible without
            opening every lesson in every course to check. */}
        {(!lesson.exercise_file_url || !lesson.resource_links?.length) && (
          <div className="flex items-center gap-1.5 mt-1">
            {!lesson.exercise_file_url && (
              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">Missing exercise</span>
            )}
            {!lesson.resource_links?.length && (
              <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">Missing resources</span>
            )}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button onClick={() => setEditing(true)} className="text-xs font-medium text-reset-terracotta hover:underline px-2 py-1">
          Edit
        </button>
        <button onClick={remove} className="text-xs font-medium text-red-400 hover:text-red-600 px-2 py-1">
          Delete
        </button>
      </div>
    </div>
  );
}

// ─── Lesson form (create or edit) ────────────────────────────────────────────

function LessonForm({ lesson, courseId, nextOrder, onSaved, onCancel }) {
  const isEdit = !!lesson;
  const [displayOrder, setDisplayOrder] = useState(lesson?.display_order ?? nextOrder ?? 1);
  const [sectionTitle, setSectionTitle] = useState(lesson?.section_title || '');
  const [lessonInstructor, setLessonInstructor] = useState(lesson?.instructor_name || '');
  const [title, setTitle] = useState(lesson?.title || '');
  const [vimeoInput, setVimeoInput] = useState(
    lesson?.video_vimeo_id
      ? (lesson.video_vimeo_hash ? `${lesson.video_vimeo_id}/${lesson.video_vimeo_hash}` : lesson.video_vimeo_id)
      : ''
  );
  const [bunnyVideoId, setBunnyVideoId] = useState(lesson?.video_bunny_id || '');
  const [lessonImageUrl, setLessonImageUrl] = useState(lesson?.lesson_image_url || '');
  const [imageAltText, setImageAltText] = useState(lesson?.resolved_image_alt_text || '');
  const [durationMinutes, setDurationMinutes] = useState(
    lesson?.video_duration_seconds ? Math.round(lesson.video_duration_seconds / 60) : ''
  );
  const [transcript, setTranscript] = useState(lesson?.transcript || '');
  const [exerciseFileUrl, setExerciseFileUrl] = useState(lesson?.exercise_file_url || '');
  const [audioUrl, setAudioUrl] = useState(lesson?.audio_url || '');
  const [skipSignedIn, setSkipSignedIn] = useState(!!lesson?.skip_once_signed_in);
  const [resourceLinks, setResourceLinks] = useState(
    lesson?.resource_links?.length ? lesson.resource_links.map(r => ({
      title: typeof r === 'string' ? '' : (r.title || r.label || ''),
      url: typeof r === 'string' ? r : (r.url || ''),
    })) : []
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleImageUrlBlur = async () => {
    const url = lessonImageUrl.trim();
    if (!url || imageAltText.trim()) return;
    try {
      const saved = await getSavedImageAltText(url);
      if (saved) setImageAltText(saved);
    } catch { /* not critical — can still type it in */ }
  };

  const addResource = () => setResourceLinks([...resourceLinks, { title: '', url: '' }]);
  const updateResource = (i, field, value) => {
    const next = [...resourceLinks];
    next[i] = { ...next[i], [field]: value };
    setResourceLinks(next);
  };
  const removeResource = (i) => setResourceLinks(resourceLinks.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    setError(null);
    const { id: vimeoId, hash: vimeoHash } = parseVimeoInput(vimeoInput);
    const fields = {
      displayOrder: Number(displayOrder) || 1,
      display_order: Number(displayOrder) || 1, // create + update use different key casing server-side
      title: title.trim(),
      videoVimeoId: vimeoId,
      video_vimeo_id: vimeoId,
      videoVimeoHash: vimeoHash,
      video_vimeo_hash: vimeoHash,
      videoBunnyId: bunnyVideoId.trim() || null,
      video_bunny_id: bunnyVideoId.trim() || null,
      lessonImageUrl: lessonImageUrl.trim() || null,
      lesson_image_url: lessonImageUrl.trim() || null,
      imageAltText: imageAltText.trim() || null,
      videoDurationSeconds: durationMinutes ? Number(durationMinutes) * 60 : null,
      video_duration_seconds: durationMinutes ? Number(durationMinutes) * 60 : null,
      transcript: transcript.trim() || null,
      exerciseFileUrl: exerciseFileUrl.trim() || null,
      exercise_file_url: exerciseFileUrl.trim() || null,
      audioUrl: audioUrl.trim() || null,
      audio_url: audioUrl.trim() || null,
      skip_once_signed_in: skipSignedIn,
      resourceLinks: resourceLinks.filter(r => r.url.trim()),
      resource_links: resourceLinks.filter(r => r.url.trim()),
      sectionTitle: sectionTitle.trim() || null,
      section_title: sectionTitle.trim() || null,
      instructorName: lessonInstructor.trim() || null,
      instructor_name: lessonInstructor.trim() || null,
    };
    try {
      if (isEdit) {
        await updateAdminLesson(lesson.id, fields);
      } else {
        await createAdminLesson(courseId, fields);
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not save this lesson.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl shadow-sm p-5 space-y-3 mb-3">
      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-xs">{error}</div>}

      <div className="grid sm:grid-cols-[80px_1fr] gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Order</label>
          <input
            type="number" value={displayOrder} onChange={e => setDisplayOrder(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Title</label>
          <input
            value={title} onChange={e => setTitle(e.target.value)} required
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
          />
        </div>
      </div>

      {/* Jenn, 2026-10-26: a lesson may be taught by someone other than the course's lead instructor. */}
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Instructor for this lesson (optional — leave blank to use the course's instructor)</label>
        <input
          value={lessonInstructor} onChange={e => setLessonInstructor(e.target.value)} placeholder="Who teaches this lesson"
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Section (optional — groups lessons in the sidebar)</label>
        <input
          value={sectionTitle} onChange={e => setSectionTitle(e.target.value)}
          placeholder="e.g. Section 1: Foundations"
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Lesson image — drag a picture in (used as the lesson's thumbnail in the sidebar, and as a poster when there's no video yet). Leave blank to use the video's own thumbnail automatically.</label>
        <input
          value={lessonImageUrl} onChange={e => setLessonImageUrl(e.target.value)} onBlur={handleImageUrlBlur}
          placeholder="https://…"
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
        />
        <MediaDropZone kind="image" onUploaded={setLessonImageUrl} />
        {lessonImageUrl && (
          <img src={previewImageUrl(lessonImageUrl)} alt="" className="mt-2 w-36 aspect-video object-contain bg-reset-terracotta/10 rounded-lg border border-gray-200" />
        )}
      </div>

      {lessonImageUrl.trim() && (
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Image alt text (describe the image for screen readers — remembered per picture, reused automatically)</label>
          <input
            value={imageAltText} onChange={e => setImageAltText(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
          />
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Video — drag the video file in (it goes to the Video Library), or paste a video ID from there</label>
          <input
            value={bunnyVideoId} onChange={e => setBunnyVideoId(e.target.value)}
            placeholder="e.g. a1b2c3d4-..."
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
          />
          <MediaDropZone kind="video" videoTitle={title} onUploaded={setBunnyVideoId} />
          <VideoTopicPicker currentId={bunnyVideoId.trim()} onPick={setBunnyVideoId} />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Duration (minutes)</label>
          <input
            type="number" value={durationMinutes} onChange={e => setDurationMinutes(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Vimeo link — legacy, only needed if this lesson isn't on Bunny yet</label>
        <input
          value={vimeoInput} onChange={e => setVimeoInput(e.target.value)}
          placeholder="e.g. https://vimeo.com/1219022284/a457522610"
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Lesson</label>
        <RichTextEditor value={transcript} onChange={setTranscript} minHeightClass="min-h-[400px]" />
      </div>

      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Exercise file — drag a PDF, Word, PowerPoint, Excel or picture in (a link still works too)</label>
        <input
          value={exerciseFileUrl} onChange={e => setExerciseFileUrl(e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
        />
        <MediaDropZone kind="file" onUploaded={setExerciseFileUrl} />
      </div>

      {/* Jenn, 2026-10-26: lessons can carry audio (e.g. a meditation or a talk); members get a player on the lesson. */}
      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Audio (optional) — drag an MP3, M4A or WAV in (a link still works too)</label>
        <input
          value={audioUrl} onChange={e => setAudioUrl(e.target.value)}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
        />
        <MediaDropZone kind="audio" onUploaded={setAudioUrl} />
        {audioUrl && <audio controls src={audioUrl} className="mt-2 w-full" />}
      </div>

      {/* Jenn, 2026-10-27: the sign-in how-to is skipped for anyone already in the dashboard. */}
      <label className="flex items-start gap-2 text-sm text-gray-700">
        <input type="checkbox" checked={skipSignedIn} onChange={e => setSkipSignedIn(e.target.checked)} className="mt-1" />
        <span>Skip this lesson for members who are already signed in<span className="block text-xs text-gray-500">For a lesson about signing in. The welcome and "next lesson" go straight past it; it stays in the course to watch any time.</span></span>
      </label>

      <div>
        <label className="block text-xs font-medium text-gray-500 mb-1">Recommended resources</label>
        <div className="space-y-2">
          {resourceLinks.map((r, i) => (
            <div key={i} className="flex gap-2">
              <input
                value={r.title} onChange={e => updateResource(i, 'title', e.target.value)}
                placeholder="Label"
                className="w-1/3 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
              />
              <input
                value={r.url} onChange={e => updateResource(i, 'url', e.target.value)}
                placeholder="https://…"
                className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
              />
              <button type="button" onClick={() => removeResource(i)} className="text-red-400 hover:text-red-600 text-sm px-2">✕</button>
            </div>
          ))}
          <button type="button" onClick={addResource} className="text-xs font-medium text-reset-terracotta hover:underline">
            + Add resource
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          type="submit" disabled={saving}
          className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50"
        >
          {saving ? 'Saving…' : isEdit ? 'Save lesson' : 'Add lesson'}
        </button>
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-gray-500 hover:text-reset-brown">
          Cancel
        </button>
      </div>
    </form>
  );
}
